import { useEffect, useMemo, useState } from 'react';
import { Ban, CheckCircle2, Hourglass, Info, Mail, Send, Search, Shield, UserPlus, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { InviteEvaluatorDialog } from '../../components/modals/invite-evaluator-dialog';
import { RevokeEvaluatorDialog } from '../../components/modals/revoke-evaluator-dialog';
import {
  useCommitteeInvites,
  useCommitteeMembers,
  useResendInvite,
  type CommitteeMemberItem,
} from '@/hooks/use-committee';

interface CommitteeEvaluatorsPageProps {
  eventId: string;
  canSendRecognitions?: boolean;
}

interface RecognitionPreview {
  eventId: string;
  eventName: string;
  templateKey: string;
  templateName: string;
  placeholders: string[];
  eligible: number;
  alreadyRecognized: number;
  pendingToSend: number;
  omitted: number;
  evaluationsSubmitted: number;
  evaluators: Array<{
    evaluatorId: string;
    name: string;
    emailMasked: string;
    evaluationsSubmitted: number;
    lastActivityAt: string | null;
    recognitionStatus: 'PENDING' | 'RECOGNIZED';
  }>;
}

interface RecognitionSendResult {
  success: boolean;
  status: string;
  eligible: number;
  alreadyRecognized: number;
  queued: number;
  skipped: number;
  failed: number;
  results: Array<{
    name: string;
    status: string;
    reason: string;
  }>;
}

function formatDate(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function authBadgeClass(provider: 'LDAP' | 'LOCAL') {
  if (provider === 'LDAP') return 'bg-blue-100 text-blue-700 hover:bg-blue-100';
  return 'bg-gray-100 text-gray-700 hover:bg-gray-100';
}

function memberBadgeClass(status: CommitteeMemberItem['status']) {
  if (status === 'ACTIVE') return 'bg-green-100 text-green-700 hover:bg-green-100';
  if (status === 'PENDING') return 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100';
  return 'bg-red-100 text-red-700 hover:bg-red-100';
}

function recognitionBadgeClass(status: string) {
  if (status === 'PENDING' || status === 'QUEUED') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (status === 'RECOGNIZED' || status === 'SENT') return 'border-green-200 bg-green-50 text-green-700';
  if (status === 'SKIPPED') return 'border-slate-200 bg-slate-50 text-slate-700';
  if (status === 'FAILED') return 'border-red-200 bg-red-50 text-red-700';
  return 'border-gray-200 bg-gray-50 text-gray-700';
}

export function CommitteeEvaluatorsPage({ eventId, canSendRecognitions = false }: CommitteeEvaluatorsPageProps) {
  const [search, setSearch] = useState('');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isResultOpen, setIsResultOpen] = useState(false);
  const [preview, setPreview] = useState<RecognitionPreview | null>(null);
  const [sendResult, setSendResult] = useState<RecognitionSendResult | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSendingRecognitions, setIsSendingRecognitions] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<{
    memberId: string;
    name: string;
    email: string;
  } | null>(null);

  const { data: members = [], isLoading: isLoadingMembers } = useCommitteeMembers(eventId);
  const { data: invites = [] } = useCommitteeInvites(eventId);
  const resendInvite = useResendInvite(eventId);

  const loadRecognitionPreview = async (openModal = false) => {
    if (!eventId || !canSendRecognitions) return;
    setIsLoadingPreview(true);
    try {
      const { data } = await api.get<RecognitionPreview>('/admin/evaluators/recognitions/preview', {
        params: { eventId },
        headers: { 'X-Passive-Request': 'true' },
      });
      setPreview(data);
      if (openModal) setIsPreviewOpen(true);
    } catch {
      toast.error('No fue posible cargar la vista previa de reconocimientos.');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  useEffect(() => {
    if (canSendRecognitions && eventId) {
      loadRecognitionPreview(false);
    }
  }, [canSendRecognitions, eventId]);

  const sendRecognitions = async () => {
    if (!eventId || !preview || preview.pendingToSend <= 0) return;
    setIsSendingRecognitions(true);
    try {
      const { data } = await api.post<RecognitionSendResult>('/admin/evaluators/recognitions/send', { eventId });
      setSendResult(data);
      setIsPreviewOpen(false);
      setIsResultOpen(true);
      await loadRecognitionPreview(false);
    } catch {
      toast.error('No fue posible encolar los reconocimientos.');
    } finally {
      setIsSendingRecognitions(false);
    }
  };

  const pendingInviteByEmail = useMemo(() => {
    const map = new Map<string, string>();
    invites
      .filter((invite) => invite.status === 'PENDING')
      .forEach((invite) => map.set(invite.email.toLowerCase(), invite.id));
    return map;
  }, [invites]);

  const filteredMembers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return members;
    return members.filter((member) => {
      const displayName = member.user.displayName.toLowerCase();
      const email = member.user.email.toLowerCase();
      return displayName.includes(normalizedSearch) || email.includes(normalizedSearch);
    });
  }, [members, search]);

  const handleResend = async (member: CommitteeMemberItem) => {
    const inviteId = pendingInviteByEmail.get(member.user.email.toLowerCase());
    if (!inviteId) {
      toast.error('No se encontró una invitación pendiente para este evaluador.');
      return;
    }

    const confirmed = window.confirm(`¿Desea reenviar la invitación a ${member.user.email}?`);
    if (!confirmed) return;

    try {
      await resendInvite.mutateAsync(inviteId);
      toast.success('Invitación reenviada correctamente.');
    } catch (error: any) {
      if (error?.response?.status === 429) {
        toast.error('Demasiados reenvíos. Intente más tarde.');
        return;
      }
      toast.error('No fue posible reenviar la invitación. Intente de nuevo.');
    }
  };

  if (!eventId) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-gray-500">
          Seleccione un evento para gestionar evaluadores.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {canSendRecognitions && (
        <Card className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700">
                  <Mail className="h-8 w-8" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#0f2744]">Reconocimientos a evaluadores</h2>
                  <p className="mt-1 max-w-2xl text-sm text-gray-600">
                    Envía un correo de agradecimiento a los evaluadores que ya registraron al menos una evaluación en este evento.
                  </p>
                </div>
              </div>
              <Button
                className="bg-[#0f6eea] text-white hover:bg-[#0d5fc7]"
                disabled={isLoadingPreview || !eventId || (preview?.pendingToSend ?? 0) <= 0}
                onClick={() => loadRecognitionPreview(true)}
              >
                {isLoadingPreview ? <Hourglass className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Revisar reconocimientos
              </Button>
            </div>
            <div className="mt-5 grid gap-3 md:grid-cols-4">
              <RecognitionMetric title="Evaluadores participantes" value={preview?.eligible ?? 0} icon={<Users className="h-6 w-6" />} tone="bg-blue-50 text-blue-700" />
              <RecognitionMetric title="Ya reconocidos" value={preview?.alreadyRecognized ?? 0} icon={<CheckCircle2 className="h-6 w-6" />} tone="bg-green-50 text-green-700" />
              <RecognitionMetric title="Pendientes de reconocimiento" value={preview?.pendingToSend ?? 0} icon={<Hourglass className="h-6 w-6" />} tone="bg-amber-50 text-amber-700" />
              <RecognitionMetric title="Evaluaciones registradas" value={preview?.evaluationsSubmitted ?? 0} icon={<Info className="h-6 w-6" />} tone="bg-violet-50 text-violet-700" />
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-lg border border-border bg-white shadow-sm">
        <CardHeader className="space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle className="text-2xl font-bold text-[#1e3a5f]">Evaluadores Asignados</CardTitle>
            <p className="text-sm text-gray-500">Gestión de evaluadores para el evento seleccionado.</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar evaluador..."
                className="pl-9 sm:w-64"
              />
            </div>
            <Button className="bg-[#c9a227] text-white hover:bg-[#b8921f]" onClick={() => setIsInviteDialogOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              + Invitar Evaluador
            </Button>
          </div>
        </div>
        </CardHeader>

        <CardContent>
        {isLoadingMembers ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, idx) => (
              <div key={`member-skeleton-${idx}`} className="grid grid-cols-7 gap-3">
                {Array.from({ length: 7 }).map((__, jdx) => (
                  <Skeleton key={`member-skeleton-cell-${idx}-${jdx}`} className="h-10 w-full" />
                ))}
              </div>
            ))}
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 text-center">
            <div className="rounded-full bg-gray-100 p-6">
              <Users className="h-16 w-16 text-[#9ca8b8]" />
            </div>
            <h3 className="text-3xl font-bold text-[#0f2744]">Aún no hay evaluadores asignados a este evento.</h3>
            <p className="text-gray-500">Invita evaluadores para comenzar el proceso de revisión académica.</p>
            <Button className="bg-[#c9a227] text-white hover:bg-[#b8921f]" onClick={() => setIsInviteDialogOpen(true)}>
              + Invitar Evaluador
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Proveedor Auth</TableHead>
                  <TableHead>Estado Membresía</TableHead>
                  <TableHead>Fecha Invitación</TableHead>
                  <TableHead>Fecha Activación</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.map((member) => {
                  const canResend = member.status === 'PENDING' && pendingInviteByEmail.has(member.user.email.toLowerCase());
                  const canRevoke = member.status === 'PENDING' || member.status === 'ACTIVE';

                  return (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">{member.user.displayName || member.user.email}</TableCell>
                      <TableCell>{member.user.email}</TableCell>
                      <TableCell>
                        <Badge className={authBadgeClass(member.user.authProvider)}>{member.user.authProvider}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={memberBadgeClass(member.status)}>{member.status}</Badge>
                      </TableCell>
                      <TableCell>{formatDate(member.invitedAt)}</TableCell>
                      <TableCell>{formatDate(member.joinedAt)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            title={canResend ? 'Reenviar invitación' : 'Reenviar deshabilitado'}
                            disabled={!canResend || resendInvite.isPending}
                            onClick={() => handleResend(member)}
                            className={!canResend ? 'cursor-not-allowed opacity-40' : ''}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="outline"
                            title={canRevoke ? 'Revocar acceso' : 'Revocación deshabilitada'}
                            disabled={!canRevoke}
                            onClick={() =>
                              setRevokeTarget({
                                memberId: member.id,
                                name: member.user.displayName || member.user.email,
                                email: member.user.email,
                              })
                            }
                            className={!canRevoke ? 'cursor-not-allowed opacity-40' : 'text-destructive'}
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        </CardContent>

        <InviteEvaluatorDialog open={isInviteDialogOpen} eventId={eventId} onOpenChange={setIsInviteDialogOpen} />

        <RevokeEvaluatorDialog
          open={Boolean(revokeTarget)}
          eventId={eventId}
          target={revokeTarget}
          onOpenChange={(open: boolean) => {
            if (!open) setRevokeTarget(null);
          }}
        />
      </Card>

      <RecognitionPreviewDialog
        open={isPreviewOpen}
        preview={preview}
        isSending={isSendingRecognitions}
        onOpenChange={setIsPreviewOpen}
        onSend={sendRecognitions}
      />
      <RecognitionResultDialog
        open={isResultOpen}
        result={sendResult}
        onOpenChange={setIsResultOpen}
        onRefresh={() => loadRecognitionPreview(false)}
      />
    </div>
  );
}

function RecognitionMetric({ title, value, icon, tone }: { title: string; value: number; icon: React.ReactNode; tone: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${tone}`}>{icon}</div>
      <div>
        <p className="text-xs font-semibold text-[#0f2744]">{title}</p>
        <p className="mt-1 text-2xl font-bold text-[#0f2744]">{value}</p>
      </div>
    </div>
  );
}

function RecognitionPreviewDialog({
  open,
  preview,
  isSending,
  onOpenChange,
  onSend,
}: {
  open: boolean;
  preview: RecognitionPreview | null;
  isSending: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#0f2744]">Enviar reconocimientos a evaluadores</DialogTitle>
          <DialogDescription>{preview?.eventName || 'Evento seleccionado'}</DialogDescription>
        </DialogHeader>
        {preview && (
          <div className="space-y-5">
            <Alert className="border-blue-200 bg-blue-50 text-blue-900">
              <Info className="h-4 w-4" />
              <AlertDescription>
                Se enviará un correo de reconocimiento únicamente a los evaluadores que registraron al menos una evaluación y que aún no han sido reconocidos.
              </AlertDescription>
            </Alert>
            <div className="grid gap-3 md:grid-cols-4">
              <RecognitionMetric title="Elegibles" value={preview.eligible} icon={<Users className="h-6 w-6" />} tone="bg-blue-50 text-blue-700" />
              <RecognitionMetric title="Ya reconocidos" value={preview.alreadyRecognized} icon={<CheckCircle2 className="h-6 w-6" />} tone="bg-green-50 text-green-700" />
              <RecognitionMetric title="Pendientes por enviar" value={preview.pendingToSend} icon={<Send className="h-6 w-6" />} tone="bg-amber-50 text-amber-700" />
              <RecognitionMetric title="Omitidos" value={preview.omitted} icon={<X className="h-6 w-6" />} tone="bg-slate-50 text-slate-700" />
            </div>
            <div className="rounded-lg border border-gray-200">
              <div className="border-b border-gray-100 p-3">
                <h3 className="font-semibold text-[#0f2744]">Preview de destinatarios</h3>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Evaluador</TableHead>
                      <TableHead>Correo</TableHead>
                      <TableHead>Evaluaciones enviadas</TableHead>
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.evaluators.map((evaluator) => (
                      <TableRow key={evaluator.evaluatorId}>
                        <TableCell>{evaluator.name}</TableCell>
                        <TableCell>{evaluator.emailMasked}</TableCell>
                        <TableCell>{evaluator.evaluationsSubmitted}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={recognitionBadgeClass(evaluator.recognitionStatus)}>
                            {evaluator.recognitionStatus === 'RECOGNIZED' ? 'Ya reconocido' : 'Pendiente'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="border-t border-gray-100 p-3 text-sm text-blue-800">Los correos se muestran enmascarados por seguridad.</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-[#0f2744]">Plantilla usada</p>
              <p className="mt-1 text-lg font-bold text-[#0f2744]">{preview.templateName}</p>
              <p className="mt-1 text-xs text-gray-600">Key: <span className="font-mono">{preview.templateKey}</span></p>
              <div className="mt-3 flex flex-wrap gap-2">
                {preview.placeholders.map((placeholder) => (
                  <Badge key={placeholder} variant="outline" className="border-violet-200 bg-violet-50 text-violet-700">{placeholder}</Badge>
                ))}
              </div>
            </div>
            <Alert className="border-amber-200 bg-amber-50 text-amber-900">
              <Shield className="h-4 w-4" />
              <AlertDescription>Esta acción enviará correos reales y quedará registrada en auditoría.</AlertDescription>
            </Alert>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSending}>Cancelar</Button>
          <Button className="bg-[#0f6eea] text-white hover:bg-[#0d5fc7]" onClick={onSend} disabled={!preview || preview.pendingToSend <= 0 || isSending}>
            {isSending ? <Hourglass className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Enviar {preview?.pendingToSend ?? 0} reconocimientos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecognitionResultDialog({
  open,
  result,
  onOpenChange,
  onRefresh,
}: {
  open: boolean;
  result: RecognitionSendResult | null;
  onOpenChange: (open: boolean) => void;
  onRefresh: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#0f2744]">Reconocimientos encolados</DialogTitle>
          <DialogDescription>El envío se procesará de forma controlada.</DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-5">
            <Alert className="border-green-200 bg-green-50 text-green-900">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>Los reconocimientos fueron registrados para envío. El sistema evitará duplicados automáticamente.</AlertDescription>
            </Alert>
            <div className="grid gap-3 md:grid-cols-4">
              <RecognitionMetric title="Elegibles" value={result.eligible} icon={<Users className="h-6 w-6" />} tone="bg-blue-50 text-blue-700" />
              <RecognitionMetric title="Encolados" value={result.queued} icon={<Send className="h-6 w-6" />} tone="bg-sky-50 text-sky-700" />
              <RecognitionMetric title="Omitidos por duplicado" value={result.skipped} icon={<X className="h-6 w-6" />} tone="bg-slate-50 text-slate-700" />
              <RecognitionMetric title="Fallidos" value={result.failed} icon={<Info className="h-6 w-6" />} tone="bg-red-50 text-red-700" />
            </div>
            <div className="rounded-lg border border-gray-200">
              <div className="border-b border-gray-100 p-3">
                <h3 className="font-semibold text-[#0f2744]">Resultado del envío</h3>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Evaluador</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Motivo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.results.map((item, index) => (
                      <TableRow key={`${item.name}-${index}`}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={recognitionBadgeClass(item.status)}>{item.status === 'QUEUED' ? 'Encolado' : item.status}</Badge>
                        </TableCell>
                        <TableCell>{item.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="border-t border-gray-100 p-3 text-sm text-blue-800">Los correos se enviarán de forma controlada en segundo plano.</p>
            </div>
            <Alert className="border-blue-200 bg-blue-50 text-blue-900">
              <Info className="h-4 w-4" />
              <AlertDescription>Esta acción quedó registrada en auditoría. La trazabilidad individual se conserva en el historial de reconocimientos.</AlertDescription>
            </Alert>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          <Button className="bg-[#0f6eea] text-white hover:bg-[#0d5fc7]" onClick={() => { onRefresh(); onOpenChange(false); }}>Actualizar lista</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
